import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { IconRegistryService } from '@shared/services/icon-registry.service';
import { CustomIcons } from '@shared/models/custom-icons';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { MainPageState } from 'app/modules/main/store/main-page.state';
import { Observable, Subject, timer } from 'rxjs';
import { SmallCardData } from 'app/modules/main/models/smal-card';
import { GetCatalogCountAndTime, GetPopularDatasets, GetSeasonDatasets, SetChosenCatalogOnMap } from 'app/modules/main/store/main-page.action';
import { MobileApp } from 'app/modules/main/models/mobile-apps';
import { GeoDataInfo } from '@shared/components/map/models/map';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { GetSelectCatalogItem, SelectCatalogItemShared } from 'app/modules/data/store/catalog/catalog.action';
import { MapCarouselWrapperComponent } from 'app/modules/main/components/map-carousel-wrapper/map-carousel-wrapper.component';
import { take, takeUntil, tap } from 'rxjs/operators';
import { CatalogInfo } from '@shared/models/shared-catalog.state';
import { ActivatedRoute, Router } from '@angular/router';
import { GetCatalogInfo, GetSelectCatalog } from '@shared/state/catalog/shared-catalog.action';
import { MapComponent } from '@shared/components/map/components/map/map.component';
import { AuthState } from '@auth/store/auth/auth.state';
import { UserInfo } from '@auth/models/auth.state';
import { Locale } from '@shared/models/locale';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedState } from '@shared/state/shared/shared.state';
import { GetCategories, GetPopularApps, GetRecommendationCatalogItemsForUser } from '@shared/state/shared/shared.action';
import { DOCUMENT, Location } from '@angular/common';

import { setLocationQueryParams } from '@utils/other-utils';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { environment } from '@environments/environment';
import { Stend } from '@shared/models/Stend';
import {
  civiliansTextEn,
  civiliansTextRu,
  developersTextEn,
  developersTextRu,
  expertTextEn,
  expertTextRu,
  headerTextEn,
  headerTextRu
} from './models';
import { MainPageService } from '../../services/main-page.service';
import { NextToMeData } from '../../../data/modules/catalog-items-data/models/catalog-item.service';
import { CatalogFilter } from '../../../data/modules/catalog-data/models/catalog.service';
import { SmallCardBlockComponent } from '../small-card-block/small-card-block.component';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class MainComponent implements OnInit, AfterViewInit {
  public headerText = '';

  public componentDestroy: () => Subject<any>;
  public expertText = '';
  public civiliansText = '';
  public residentsParams: CatalogFilter;
  public developersText = '';
  public catalogFilter: CatalogFilter;
  public isMapOpen: boolean | null = false;
  public isDatasetOpen: boolean | null = false;
  public icons = CustomIcons;
  public isRuLocale = true;
  public langChange$: Observable<LangChangeEvent>;
  public breakPointObserve$ = this.breakpointObserver.observe(['(min-width: 941px)']);
  public isRecommendationData = false;
  @Select(actionsExecuting([GetSeasonDatasets])) public loadingSeasonsData$: Observable<ActionsExecuting>;
  @Select(MainPageState.getPopularDatasets) public popularDatasets$: Observable<SmallCardData[] | null>;
  public seasonDatasets$: Observable<SmallCardData[] | null>;
  @Select(MainPageState.seasonDatasetsCriteria) public seasonDatasetsCriteria$: Observable<CatalogFilter | null>;
  @Select(SharedState.getPopularApps) public popularApps$: Observable<MobileApp[] | null>;
  @Select(MainPageState.getCatalogsCount) public catalogCount$: Observable<number>;
  @Select(SharedCatalogState.geoData) public currentMap$: Observable<GeoDataInfo[]>;
  @Select(SharedCatalogState.catalogInfo) public catalogInfo$: Observable<CatalogInfo | undefined>;
  @Select(AuthState.authenticatedUser) public isAuthenticated$: Observable<UserInfo | null>;

  @ViewChild(MapCarouselWrapperComponent) public carouselMapWrapper?: MapCarouselWrapperComponent;
  @ViewChild(MapComponent) public carouselMapComponent?: MapComponent;
  @ViewChild('map') public carouselMapRef?: ElementRef<HTMLElement>;

  @ViewChild(SmallCardBlockComponent) public smallCardBlockComponent?: SmallCardBlockComponent;
  public defaultIsNextMeData: NextToMeData;

  constructor(
    iconRegistry: IconRegistryService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private store: Store,
    private router: Router,
    private location: Location,
    private activeRouter: ActivatedRoute,
    private translateService: TranslateService,
    public mainPageService: MainPageService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    iconRegistry.addIconPack([CustomIcons.MainHeaderPeople, CustomIcons.Residents, CustomIcons.Developers, CustomIcons.Experts]);
  }

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
      this.expertText = expertTextRu;
      this.headerText = headerTextRu;
      this.civiliansText = civiliansTextRu;
      this.developersText = developersTextRu;
    } else {
      this.isRuLocale = false;
      this.expertText = expertTextEn;
      this.headerText = headerTextEn;
      this.civiliansText = civiliansTextEn;
      this.developersText = developersTextEn;
    }
  }

  private showMap(): Observable<any> {
    this.carouselMapWrapper?.mapOpen.next(true);

    return timer(1000).pipe(take(1));
  }

  public ngAfterViewInit(): void {
    this.document.body.style.overflow = 'auto';
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);
    const params = this.activeRouter.snapshot.queryParams;
    let recommendationCatalogId = this.mainPageService.isRecommendationId;
    const catalogGlobalId = params?.catalogGlobalId;
    const catalogItemGlobalId = params?.catalogItemGlobalId;
    if (params.recommendations) {
      recommendationCatalogId = +params.recommendations;
      this.isRecommendationData = true;
    }
    if (this.isRecommendationData && !user) {
      this.showMap().subscribe(() => {
        this.carouselMapRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        setTimeout(() => {
          const user = this.store.selectSnapshot(AuthState.authenticatedUser);
          if (!user) {
            const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
            authorizationDialog.componentInstance.keyOperations = 'GetRecommendation';
            authorizationDialog.componentInstance.operationValue = recommendationCatalogId;
            authorizationDialog
              .afterClosed()
              .pipe(take(1), takeUntil(this.componentDestroy()))
              .subscribe(res => {
                if (!res) {
                  this.isRecommendationData = false;
                  setLocationQueryParams(
                    {
                      recommendations: null
                    },
                    this.router,
                    this.location
                  );
                  this.cdr.detectChanges();
                }
              });
          } else if (recommendationCatalogId) {
            this.store
              .dispatch([
                new GetSelectCatalog(recommendationCatalogId),
                new GetCatalogInfo(recommendationCatalogId),
                new SetChosenCatalogOnMap(recommendationCatalogId),
                new GetRecommendationCatalogItemsForUser(recommendationCatalogId)
              ])
              .pipe(take(1))
              .subscribe(() => {
                this.carouselMapComponent?.getRecommendation(true);
              });
          }
        }, 1000);
      });

      return;
    }

    if (recommendationCatalogId) {
      this.store
        .dispatch([
          new GetSelectCatalog(recommendationCatalogId),
          new GetCatalogInfo(recommendationCatalogId),
          new SetChosenCatalogOnMap(recommendationCatalogId),
          new GetRecommendationCatalogItemsForUser(recommendationCatalogId)
        ])
        .pipe(take(1))
        .subscribe(() => {
          this.carouselMapComponent?.getRecommendation(true);
        });

      this.showMap().subscribe(() => {
        this.carouselMapRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    } else if (catalogGlobalId && catalogItemGlobalId) {
      this.showMap().subscribe(() => {
        this.carouselMapRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  }

  public openAuthorizationDialog(): void {
    this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
  }

  public showCard(globalId: number | null): void {
    const catalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    if (globalId) {
      this.store.dispatch(new GetSelectCatalogItem(globalId, catalog.id, catalog.isDynamic));
    } else {
      this.store.dispatch(new SelectCatalogItemShared(globalId));
    }
  }

  public onMapCardClick(): void {
    this.carouselMapWrapper?.mapOpen.next(true);
    this.store.dispatch(new SelectCatalogItemShared(null));
  }

  public onRecommendationClick(isRecomendation: boolean): void {
    const catalogId = this.store.selectSnapshot(MainPageState.getChosenCatalogOnMap);
    if (isRecomendation && catalogId) {
      this.mainPageService.isRecommendationId = +catalogId;
      setLocationQueryParams(
        {
          recommendations: catalogId
        },
        this.router,
        this.location
      );
    } else {
      this.mainPageService.isRecommendationId = null;
      setLocationQueryParams(
        {
          recommendations: null
        },
        this.router,
        this.location
      );
    }
  }

  public onNextMeDataClick($event: NextToMeData): void {
    this.mainPageService.isNextMeData = $event;
  }

  public ngOnInit(): void {
    switch (environment.stend) {
      case Stend.Dev:
        this.catalogFilter = {
          newRecommendedFavorites: 'all',
          openCategoryIds: [120, 14]
        };
        break;
      case Stend.Prod:
        this.catalogFilter = {
          newRecommendedFavorites: 'all',
          openCategoryIds: [113, 128]
        };
        break;
      case Stend.Stage:
        this.catalogFilter = {
          newRecommendedFavorites: 'all',
          openCategoryIds: [178, 127]
        };
        break;
      default:
        this.catalogFilter = {
          newRecommendedFavorites: 'all',
          openCategoryIds: [120, 14]
        };
    }
    this.defaultIsNextMeData = this.mainPageService.isNextMeData;
    this.seasonDatasets$ = this.store.select(MainPageState.getSeasonDatasets).pipe(
      tap(res => {
        if (res && res?.length > 0) {
          const currenSeasons = res[0].seasonsId;
          if (+currenSeasons === 2) {
            this.residentsParams = {
              newRecommendedFavorites: 'all',
              booleanFilters: {
                winterSeason: true
              }
            };
          } else if (+currenSeasons === 3) {
            this.residentsParams = {
              newRecommendedFavorites: 'all',
              booleanFilters: {
                summerSeason: true
              }
            };
          } else if (+currenSeasons === 4) {
            this.residentsParams = {
              newRecommendedFavorites: 'all',
              booleanFilters: {
                allSeasons: true
              }
            };
          }
        }
      })
    );
    this.store
      .dispatch([new GetCatalogCountAndTime(), new GetCategories()])
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch([new GetPopularDatasets(), new GetSeasonDatasets(), new GetPopularApps(), new SelectCatalogItemShared(null)]);
      });

    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
  }

  public sizeConversionCard(isFloating: boolean): void {
    if (this.smallCardBlockComponent) {
      this.smallCardBlockComponent.isFloating = isFloating;
    }
  }
}
